import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../store/types";
import type { AppConfig, ConnectedInstance } from "../slices/instanceSlice";

// Root selectors
const getUserInstanceState = (state: RootState) => state.userInstance;

// ConnectedInstance Selectors
export const getConnectedInstanceSelector = createSelector(
  [getUserInstanceState],
  (userInstance) => userInstance?.connectedInstance,
);
export const selectUserInstances = createSelector(
  [getUserInstanceState],
  (userInstance) => userInstance.instances,
);

export const selectUserShowModal = createSelector(
  [getUserInstanceState],
  (userInstance) => userInstance.showModal,
);

export const selectUserStatus = createSelector(
  [getUserInstanceState],
  (userInstance) => userInstance.status,
);
export const selectCallsWaitingWarningThreshold = createSelector(
  [getUserInstanceState],
  (userInstance) =>
    (userInstance?.connectedInstance as ConnectedInstance | null)
      ?.callsWaitingWarningThreshold,
);

export const selectCallsWaitingErrorThreshold = createSelector(
  [getUserInstanceState],
  (userInstance) =>
    (userInstance?.connectedInstance as ConnectedInstance | null)
      ?.callsWaitingErrorThreshold,
);

// AppConfig Selectors
export const getAppConfigSelector = createSelector(
  [getUserInstanceState],
  (userInstance) =>
    (userInstance?.connectedInstance as ConnectedInstance | null)?.appConfig,
);

export const getVoiceSelector = createSelector(
  [getAppConfigSelector],
  (appConfig) => appConfig?.voice,
);

export const getLanguagesSelector = createSelector(
  [getAppConfigSelector],
  (appConfig) => appConfig?.languages,
);
export const getTranscriptsBucketNameSelector = createSelector(
  [getAppConfigSelector],
  (appConfig) => appConfig?.transcriptsBucketName,
);

export const getBusinessSelector = createSelector(
  [getAppConfigSelector],
  (appConfig) => appConfig?.business,
);

export const getClosureTabsSelector = createSelector(
  [getAppConfigSelector],
  (appConfig) => appConfig?.closureTabs,
);

export const getLanguageSelector = createSelector(
  [getAppConfigSelector],
  (appConfig) => appConfig?.language,
);

export const getManagerNavSelector = createSelector(
  [getAppConfigSelector],
  (appConfig) => appConfig?.managerNav,
);

export const getIsOutboundPhoneEnabledSelector = createSelector(
  [getAppConfigSelector],
  (appConfig) => appConfig?.isOutboundPhoneEnabled,
);

export const getOutboundPhonesSelector = createSelector(
  [getAppConfigSelector],
  (appConfig) => appConfig?.outboundPhones,
);
export const getResourceByLobSelector = createSelector(
  [getAppConfigSelector],
  (appConfig) => appConfig?.resourcesByLob || false,
);

export const getSenderNumberSelector = createSelector(
  [getAppConfigSelector],
  (appConfig) => appConfig?.senderNumber,
);

// Instance ID Selector
export const getInstanceIdSelector = createSelector(
  [getUserInstanceState],
  (userInstance) =>
    (userInstance?.connectedInstance as ConnectedInstance | null)?.instanceId,
);

export const getInstanceUrlSelector = createSelector(
  [getUserInstanceState],
  (userInstance) =>
    (userInstance?.connectedInstance as ConnectedInstance | null)?.instanceUrl,
);

export const getArnSelector = createSelector(
  [getUserInstanceState],
  (userInstance) =>
    (userInstance?.connectedInstance as ConnectedInstance | null)?.arn,
);
